import React, { useEffect, useRef } from 'react';
import { path, is } from 'ramda';
import styled from 'styled-components';
import { VariableSizeList } from 'react-window';

type Props = {
    children?: React.ReactNode | null;
    width?: number;
    height?: number;
    getItemHeight?: (index: number) => number;
    className?: string;
};

const List = styled.ul`
    padding: 0;
    list-style: none;
    margin: 0;
    width: 100%;
`;

const DropdownItemWrapper = styled.li`
    display: block;
    width: 100%;
    overflow: hidden;

    &:first-child {
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
    }
    &:last-child {
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
    }
`;

const getChildrenAsArray = (children?: React.ReactNode | null): React.ReactNode[] => {
    if (!children) {
        return [];
    }
    if (children && path(['type'], children) === React.Fragment) {
        return path(['props', 'children'], children) as React.ReactNode[];
    }
    if (children && is(Array, children)) {
        return children as React.ReactNode[];
    }
    return [children];
};

export const DropdownList = React.forwardRef<HTMLUListElement, Props>(
    ({ children = [], getItemHeight = () => 48, height, ...rest }, ref) => {
        // enforce array format
        const childrenArray = getChildrenAsArray(children);
        // get the height of all items
        const aggregateHeight = childrenArray.reduce((acc: number, _, i) => acc + getItemHeight(i), 0);

        const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => (
            <DropdownItemWrapper key={index} style={style}>
                {childrenArray[index]}
            </DropdownItemWrapper>
        );

        if (!height) {
            return (
                <List {...rest} ref={ref}>
                    {childrenArray.map((child, index) => (
                        <DropdownItemWrapper key={index}>{child}</DropdownItemWrapper>
                    ))}
                </List>
            );
        }

        const variableListRef = useRef<VariableSizeList | null>(null);
        /*
            Need to bust the VariableSizeList's style cache.
            Otherwise glitches (e.g. gaps) will occurr when filtering variable height items.
        */
        useEffect(() => variableListRef.current?.resetAfterIndex(0, true), [aggregateHeight]);

        // for large number of items we want to use virtual scrolling wrapper
        return (
            <VariableSizeList
                innerElementType={innerProps => <List {...innerProps} ref={ref} />}
                height={height || aggregateHeight}
                width="100%"
                itemCount={childrenArray.length}
                itemSize={getItemHeight}
                ref={variableListRef}
            >
                {Row}
            </VariableSizeList>
        );
    },
);
